<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  CONVERSATION_LAYOUT,
  normalizeConversationLayoutVersion,
} from 'dashboard/helper/conversationLayoutHelper';
import ChatListVisibilityManual from './ChatListVisibilityManual.vue';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const isUpdating = ref(false);
const showDropdown = ref(false);
const visibilityManualRef = ref(null);

const conversationLayout = computed(() => {
  return normalizeConversationLayoutVersion(
    uiSettings.value.conversation_layout_version
  );
});

const selectedLayoutLabel = computed(() => {
  return conversationLayout.value === CONVERSATION_LAYOUT.SIMPLIFIED
    ? t('CHAT_LIST.VIEW_MODE.SIMPLIFIED')
    : t('CHAT_LIST.VIEW_MODE.CLASSIC');
});

const menuItems = computed(() => [
  {
    label: t('CHAT_LIST.VIEW_MODE.CLASSIC'),
    action: 'layout',
    value: CONVERSATION_LAYOUT.CLASSIC,
    isSelected: conversationLayout.value === CONVERSATION_LAYOUT.CLASSIC,
  },
  {
    label: t('CHAT_LIST.VIEW_MODE.SIMPLIFIED'),
    action: 'layout',
    value: CONVERSATION_LAYOUT.SIMPLIFIED,
    isSelected: conversationLayout.value === CONVERSATION_LAYOUT.SIMPLIFIED,
  },
  {
    label: t('CHAT_LIST.VIEW_MODE.VISIBILITY_MANUAL'),
    action: 'manual',
    value: 'manual',
  },
]);

const toggleDropdown = () => {
  if (isUpdating.value) {
    return;
  }

  showDropdown.value = !showDropdown.value;
};

const closeDropdown = () => {
  showDropdown.value = false;
};

const updateLayoutPreference = async layout => {
  if (isUpdating.value || layout === conversationLayout.value) {
    return;
  }

  isUpdating.value = true;

  // The simplified layout collapses the secondary sidebar (as if the
  // sidemenu toggle was clicked); switching back to classic restores it.
  const showSecondarySidebar = layout !== CONVERSATION_LAYOUT.SIMPLIFIED;

  try {
    await updateUISettings(
      {
        conversation_layout_version: layout,
        show_secondary_sidebar: showSecondarySidebar,
        previously_used_sidebar_view: showSecondarySidebar,
      },
      {
        rollbackOnError: true,
        throwOnError: true,
      }
    );
  } catch (error) {
    useAlert(t('CHAT_LIST.VIEW_MODE.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const handleAction = async ({ action, value }) => {
  closeDropdown();

  if (action === 'manual') {
    visibilityManualRef.value?.open();
    return;
  }

  await updateLayoutPreference(value);
};
</script>

<template>
  <div v-on-click-outside="closeDropdown" class="relative">
    <button
      v-tooltip.top="
        `${t('CHAT_LIST.VIEW_MODE.LABEL')}: ${selectedLayoutLabel}`
      "
      type="button"
      class="inline-flex items-center justify-center rounded-md size-7 text-n-slate-11 hover:bg-n-alpha-1"
      :class="{ 'bg-n-alpha-1': showDropdown }"
      :disabled="isUpdating"
      @click="toggleDropdown"
    >
      <span class="i-lucide-ellipsis size-4" />
    </button>

    <DropdownMenu
      v-if="showDropdown"
      :menu-items="menuItems"
      class="mt-2 ltr:left-0 rtl:right-0"
      @action="handleAction"
    />

    <ChatListVisibilityManual ref="visibilityManualRef" />
  </div>
</template>
